/**
 * Подсказки по мере ввода (город рождения и т.п.): только свежие.
 *
 * Было: набрано «Харьк», а в списке ещё «Ухта, Республика Коми» — ответ на
 * прошлый запрос (или на подставленный из профиля город) приходил позже и
 * перезаписывал список, а клик по первой строке выбирал устаревший город.
 * При задержке до сервера ~0,3 с это видно на каждом вводе.
 *
 * Здесь:
 *  - запрос уходит через `delay` мс после последней клавиши;
 *  - новый ввод отменяет и таймер, и уже летящий запрос (AbortController):
 *    поздний ответ на старый текст в список не попадает никогда;
 *  - подсказки отдаются, только если это ответ на ТЕКУЩИЙ текст поля.
 *    Пока ответа на него нет — suggestions пуст, isSearching = true: прошлых
 *    вариантов в списке не видно и выбрать их нельзя.
 *
 * search(q, signal) → Promise<массив> — сам запрос (и запасные варианты,
 * если они нужны); signal передаётся в fetch/axios для отмены.
 *
 * Возвращает { suggestions, isSearching, error, query }.
 */
import { useEffect, useRef, useState } from 'react';

const EMPTY = [];

export function useCitySearch(query, { search, enabled = true, minLength = 2, delay = 300 } = {}) {
  const trimmed = String(query || '').trim();
  const active = !!enabled && trimmed.length >= minLength;
  const [answer, setAnswer] = useState({ q: null, list: EMPTY, error: null });
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    if (!active) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      let pending;
      try {
        pending = Promise.resolve(searchRef.current(trimmed, controller.signal));
      } catch (error) {
        pending = Promise.reject(error);
      }
      pending
        .then((list) => {
          if (controller.signal.aborted) return;
          setAnswer({ q: trimmed, list: Array.isArray(list) ? list : EMPTY, error: null });
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          setAnswer({ q: trimmed, list: EMPTY, error });
        });
    }, delay);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed, active, delay]);

  const fresh = active && answer.q === trimmed;
  return {
    suggestions: fresh ? answer.list : EMPTY,
    isSearching: active && !fresh,
    error: fresh ? answer.error : null,
    query: trimmed,
  };
}

export default useCitySearch;
